/**
 * Why someone is switching auto-renew off, or deleting their card.
 *
 * The codes are the WIRE format and must stay byte-identical to
 * `services/api/src/modules/billing/churn-reasons.ts` — the API validates
 * `reason` against a closed list there, so a code that drifts here is a 400 on
 * the cancel path, i.e. a buyer who cannot cancel. The two halves are separate
 * files rather than a shared package because this one carries i18n KEYS (three
 * locales, resolved at render) and the other carries ru labels for Telegram and
 * the admin panel; only the ten-item code list is genuinely common.
 *
 * ⚠️ The keys below are resolved through a computed `t(r.key)`, which
 * `i18n/keys-exist.test.ts` cannot see (it only scans literal `t('…')` calls).
 * `churn-reason-modal.test.tsx` checks every one of them against ru/uz/kk
 * instead — do not drop that test thinking parity covers it, parity only
 * compares the locales with each other.
 */

export interface ChurnReasonOption {
  code: string;
  /** i18n key for the label shown in the radio list. */
  key: string;
}

/**
 * Card-deletion-only reasons, PREPENDED to the shared list on «Удалить карту».
 * Deleting a card is not the same gesture as cancelling — the commonest answer
 * is expected to be "I am replacing it", which is not churn at all, and folding
 * it into «Другое» would make the breakdown read as a product failure.
 */
const CARD_ONLY: ChurnReasonOption[] = [
  { code: 'changing_card', key: 'churn.reason.changingCard' },
  { code: 'no_card_storage', key: 'churn.reason.noCardStorage' },
  { code: 'stop_renewing', key: 'churn.reason.stopRenewing' },
];

/**
 * Offered on BOTH sheets, in display order.
 *
 * «Другое» goes FIRST (Igor, 2026-09-21). It is the escape hatch, and an escape
 * hatch at the bottom of a thirteen-row list is one the reader has to scroll
 * past everything else to reach — on a 360×640 sheet that is the whole list.
 * Put first it costs one tap, and the free text next to it is REQUIRED (see
 * `churn-reason-modal.tsx`), so choosing it is not a way to say nothing.
 *
 * ⚠️ `declined` («Не хочу отвечать») is deliberately ABSENT. It shipped on
 * 2026-09-20 and was struck out a day later: with «Другое» first and its text
 * box mandatory, a one-tap "no answer" option is simply the cheapest row on the
 * sheet, and every tap on it is a cancellation we learn nothing from. The API
 * still ACCEPTS the code (`services/api/.../churn-reasons.ts`) so rows already
 * written keep their admin label and an old bundle in a tab can still cancel.
 */
const SHARED: ChurnReasonOption[] = [
  { code: 'other', key: 'churn.reason.other' },
  { code: 'no_relevant_tenders', key: 'churn.reason.noRelevantTenders' },
  { code: 'did_not_win', key: 'churn.reason.didNotWin' },
  { code: 'no_time', key: 'churn.reason.noTime' },
  { code: 'found_better', key: 'churn.reason.foundBetter' },
  { code: 'too_expensive', key: 'churn.reason.tooExpensive' },
  { code: 'one_off_need', key: 'churn.reason.oneOffNeed' },
  { code: 'poor_alerts', key: 'churn.reason.poorAlerts' },
  { code: 'hard_to_use', key: 'churn.reason.hardToUse' },
  { code: 'payment_issue', key: 'churn.reason.paymentIssue' },
];

/** Which sheet is asking. `autoRenew` = the checkbox, `card` = «Удалить карту». */
export type ChurnReasonKind = 'autoRenew' | 'card';

/** The options that sheet offers, in order. */
export function churnReasonOptions(kind: ChurnReasonKind): ChurnReasonOption[] {
  return kind === 'card' ? [...CARD_ONLY, ...SHARED] : SHARED;
}

/** Every option either sheet can show — for the locale-coverage test. */
export const ALL_CHURN_REASONS: ChurnReasonOption[] = [...CARD_ONLY, ...SHARED];

/** How much free text «Расскажите подробнее» accepts. Mirrors the DTO's
 *  `MaxLength(300)`; enforced here so the field simply stops rather than
 *  letting a buyer type a paragraph the server will reject. */
export const CHURN_REASON_TEXT_MAX = 300;

/** The code whose free text is MANDATORY: «Другое» answers nothing on its own. */
export const CHURN_REASON_REQUIRING_TEXT = 'other';

/**
 * The one line the sheet shows for a chosen reason — only where the fix is a
 * person: a search tuned in Telegram (Igor, 2026-09-21).
 *
 * Igor, 10.10: «дохуя текста… год за 4 млн вместо 8 — не понимаю смысл». The
 * sheet was trimmed to a short list, one value line («По вашим поискам за месяц
 * пришло N лотов») and the pause as the main button. The year-price line under
 * «Дорого» is gone (the annual plan stays on /plans), and so are the two lines
 * that only restated advice (`did_not_win`, `no_time`). The pause is offered
 * under EVERY reason now, so there is no per-reason pause list either.
 *
 * `telegram: true` means the line points at the operator's Telegram, so the
 * render site must gate it on `HAS_TELEGRAM` — a telegram-off deployment (KZ)
 * must never print «напишите в Telegram».
 */
export interface ChurnRetentionHint {
  /** i18n key for the sentence. */
  key: string;
  /** Links to `COMPANY.telegramUrl`; requires `HAS_TELEGRAM` at the render site. */
  telegram?: boolean;
}

const RETENTION_HINTS: Record<string, ChurnRetentionHint> = {
  no_relevant_tenders: { key: 'churn.retain.noRelevantTenders', telegram: true },
  poor_alerts: { key: 'churn.retain.poorAlerts', telegram: true },
  changing_card: { key: 'churn.retain.changingCard' },
};

/**
 * «Меняю карту» on the card sheet does NOT delete the card (Igor, 10.10: «сделать
 * как будто привязывает старую и меняет»). Deleting it also switched auto-renew
 * off, so a buyer who only meant to swap cards lost Pro silently on the charge
 * day. The caller opens the card-attach sheet instead — the same one «Изменить
 * карту» opens: a re-attach overwrites the card in place, the old one stays
 * until a new one is bound, auto-renew is untouched. Nothing is sent to the
 * server for this answer, so the sheet hides the free-text box under it.
 */
export const CHURN_REASON_CHANGING_CARD = 'changing_card';

/** The retention line for a chosen reason, or null when that reason has none. */
export function churnRetentionHint(code: string | null): ChurnRetentionHint | null {
  return (code && RETENTION_HINTS[code]) || null;
}
